import React, { useState, useEffect } from "react";
import { createGrammarSubTopic, updateGrammarSubTopic } from "./adminGrammarService";
import "../../styles/content.css";

const emptySection = () => ({ heading: "", content: "", example: "", sortOrder: 1 });

const GrammarSubTopicForm = ({ subTopic, grammarTopicId, onSaved, onCancel }) => {
  const isEdit = !!subTopic;
  const [form, setForm] = useState({ title: "", youtubeLink: "" });
  const [sections, setSections] = useState([emptySection()]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (subTopic) {
      console.log("[GrammarSubTopicForm] Populating form for edit, id:", subTopic.id);
      setForm({ title: subTopic.title || "", youtubeLink: subTopic.youtubeLink || "" });
      if (subTopic.sections?.length) {
        setSections(subTopic.sections.map((s) => ({
          heading: s.heading || "",
          content: s.content || "",
          example: s.example || "",
          sortOrder: s.sortOrder || 1,
        })));
      }
    }
  }, [subTopic]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSectionChange = (idx, field, value) => {
    const updated = [...sections];
    updated[idx] = { ...updated[idx], [field]: value };
    setSections(updated);
  };

  const addSection = () => setSections([...sections, { ...emptySection(), sortOrder: sections.length + 1 }]);

  const removeSection = (idx) => {
    if (sections.length <= 1) return;
    setSections(sections.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sortOrder: i + 1 })));
  };

  const handleSubmit = async () => {
    if (!form.title) {
      console.warn("[GrammarSubTopicForm] Validation failed: missing title");
      alert("Title is required");
      return;
    }
    if (sections.some((s) => !s.heading || !s.content)) {
      console.warn("[GrammarSubTopicForm] Validation failed: incomplete sections");
      alert("Each section needs a heading and content");
      return;
    }

    const payload = {
      title: form.title,
      grammarTopicId: grammarTopicId,
      ...(form.youtubeLink && { youtubeLink: form.youtubeLink }),
      sections: sections.map((s, i) => ({ ...s, sortOrder: i + 1 })),
    };

    console.log("[GrammarSubTopicForm] Submitting", isEdit ? "update" : "create", "payload:", payload);
    setLoading(true);
    const res = isEdit
      ? await updateGrammarSubTopic(subTopic.id, payload)
      : await createGrammarSubTopic(payload);
    if (res.status === 200 || res.status === 201) {
      console.log("[GrammarSubTopicForm] Submit successful:", res.status);
      onSaved();
    } else {
      console.error("[GrammarSubTopicForm] Submit failed:", res.status, res.message);
      alert(res.message || "Operation failed");
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: 640, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="form-card" style={{ margin: 0, boxShadow: "none" }}>
          <h2>{isEdit ? "Edit" : "Create"} Sub Topic</h2>

          <div className="form-group">
            <label>Title *</label>
            <input name="title" placeholder="e.g. Nouns" value={form.title} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>YouTube Link (optional)</label>
            <input name="youtubeLink" placeholder="https://youtube.com/watch?v=..." value={form.youtubeLink} onChange={handleChange} />
          </div>

          <hr style={{ margin: "16px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>Sections ({sections.length})</h3>
            <button type="button" onClick={addSection} style={{ fontSize: 13 }}>+ Add Section</button>
          </div>

          {sections.map((sec, idx) => (
            <div key={idx} style={{ border: "1px solid #eee", borderRadius: 8, padding: 12, marginTop: 12, background: "#fafafa" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <strong>Section {idx + 1}</strong>
                {sections.length > 1 && (
                  <button type="button" onClick={() => removeSection(idx)} style={{ color: "red", background: "none", border: "none", cursor: "pointer", fontSize: 13 }}>
                    ✕ Remove
                  </button>
                )}
              </div>
              <div className="form-group">
                <label>Heading *</label>
                <input placeholder="Section heading" value={sec.heading} onChange={(e) => handleSectionChange(idx, "heading", e.target.value)} />
              </div>
              <div className="form-group">
                <label>Content *</label>
                <textarea placeholder="Section content..." value={sec.content} onChange={(e) => handleSectionChange(idx, "content", e.target.value)} rows={3} />
              </div>
              <div className="form-group">
                <label>Example</label>
                <input placeholder="e.g. dog, London, happiness" value={sec.example} onChange={(e) => handleSectionChange(idx, "example", e.target.value)} />
              </div>
            </div>
          ))}

          <div className="form-actions" style={{ marginTop: 16 }}>
            <button onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </button>
            <button onClick={onCancel} disabled={loading}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrammarSubTopicForm;
